// src/oura/components/docs/write/ClientEmailEditor.tsx
//
// The client email's rich text editor (#4840): draft-js, as the house editor
// (components/rich-editor/v2), with only what the email's renderer draws:
// bold, bulleted and numbered lists, and the doc's link, which shows as a
// link and keeps its address while its words are edited. No italic, no code,
// no new link: the server's checks allow exactly one link, the doc's.
//
// It draws with the email's own spacing and font (emailStyle.ts, #4842), so a
// paragraph, a list or a line break looks here as it will in the inbox.
//
// It starts from the email as the preview shows it (the server's HTML) and
// answers its content as HTML; the dialog turns that into the email's markup
// (emailMarkup.ts) on Save.
//
// It sits in a compose window (#4853): the text fills the space it is given and
// the tools sit under it in a rounded bar, as in Gmail. Of Gmail's tools only
// the ones the email carries: undo and redo, bold, and the two lists.

import { Fragment, useRef, useState, type MouseEvent, type ReactNode } from "react";
import {
    CompositeDecorator,
    ContentState,
    Editor,
    EditorState,
    RichUtils,
    convertToRaw,
    type ContentBlock,
    type DraftEditorCommand,
    type DraftHandleValue,
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { BoldIcon, ListIcon, ListOrderedIcon, Redo2Icon, Undo2Icon } from "lucide-react";
import { cn } from "../../../lib/cn";
import { EDITOR_CSS, EMAIL_LINK } from "./emailStyle";

function linkStrategy(block: ContentBlock, callback: (start: number, end: number) => void, content: ContentState) {
    block.findEntityRanges((c) => {
        const key = c.getEntity();
        return key !== null && content.getEntity(key).getType() === "LINK";
    }, callback);
}

function LinkSpan({ children }: { children?: ReactNode }) {
    return (
        <span className="underline" style={{ color: EMAIL_LINK }}>
            {children}
        </span>
    );
}

const decorator = new CompositeDecorator([{ strategy: linkStrategy, component: LinkSpan }]);

/** The email's HTML as an editor state: blocks, bold, lists and the link's entity. */
function stateOf(html: string): EditorState {
    const parsed = htmlToDraft(html);
    if (!parsed) return EditorState.createEmpty(decorator);
    // The entity map too, or the doc's link would come in as plain words.
    const content = ContentState.createFromBlockArray(parsed.contentBlocks, parsed.entityMap);
    return EditorState.createWithContent(content, decorator);
}

const TOOLS = [
    { label: "Bold", style: "BOLD", kind: "inline", Icon: BoldIcon },
    { label: "Numbered list", style: "ordered-list-item", kind: "block", Icon: ListOrderedIcon },
    { label: "Bulleted list", style: "unordered-list-item", kind: "block", Icon: ListIcon },
] as const;

const TOOL_BUTTON = "flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-200 disabled:pointer-events-none disabled:opacity-40";

function Divider() {
    return <span className="mx-1 h-5 w-px bg-gray-300" aria-hidden />;
}

export function ClientEmailEditor({ html, onChange, disabled, labelledBy }: { html: string; onChange: (html: string) => void; disabled?: boolean; labelledBy?: string }) {
    const [state, setState] = useState<EditorState>(() => stateOf(html));
    const editor = useRef<Editor>(null);

    const change = (next: EditorState) => {
        if (disabled) return;
        setState(next);
        onChange(draftToHtml(convertToRaw(next.getCurrentContent())));
    };

    const active = (tool: (typeof TOOLS)[number]) => {
        if (tool.kind === "inline") return state.getCurrentInlineStyle().has(tool.style);
        const key = state.getSelection().getStartKey();
        return state.getCurrentContent().getBlockForKey(key)?.getType() === tool.style;
    };

    const keyCommand = (command: DraftEditorCommand, editorState: EditorState): DraftHandleValue => {
        // Only the marks the email draws: Cmd+B, never Cmd+I or Cmd+U.
        if (command === "italic" || command === "underline" || command === "code") return "handled";
        const next = RichUtils.handleKeyCommand(editorState, command);
        if (next) {
            change(next);
            return "handled";
        }
        return "not-handled";
    };

    // Keep the selection in the editor while a tool is pressed.
    const keepSelection = (e: MouseEvent) => e.preventDefault();

    return (
        <div className={cn("flex min-h-0 flex-1 flex-col bg-white", disabled && "opacity-70")}>
            {/* The email's own look (emailStyle.ts): what the editor shows is how it is sent (#4842). */}
            <style>{EDITOR_CSS}</style>
            <div
                className="client-email-editor min-h-0 flex-1 cursor-text overflow-y-auto"
                // A click in the empty space under the text puts the cursor in it, as in a mail app.
                onClick={(e) => e.target === e.currentTarget && editor.current?.focus()}
            >
                <Editor
                    ref={editor}
                    editorState={state}
                    onChange={change}
                    readOnly={disabled}
                    handleKeyCommand={keyCommand}
                    handleReturn={(e, s) => {
                        // Shift+Enter: a line break inside the paragraph ("Cheers," / "Angel").
                        if (e.shiftKey) {
                            change(RichUtils.insertSoftNewline(s));
                            return "handled";
                        }
                        return "not-handled";
                    }}
                    ariaLabelledBy={labelledBy}
                    ariaMultiline
                    spellCheck
                />
            </div>
            <div className="px-3 pb-2 pt-1">
                <div className="flex w-fit max-w-full flex-wrap items-center gap-0.5 rounded-full bg-gray-100 px-2 py-1 text-gray-600" role="toolbar" aria-label="Format">
                    <button type="button" title="Undo" aria-label="Undo" disabled={disabled || state.getUndoStack().isEmpty()} className={TOOL_BUTTON} onMouseDown={keepSelection} onClick={() => change(EditorState.undo(state))}>
                        <Undo2Icon className="h-4 w-4" aria-hidden />
                    </button>
                    <button type="button" title="Redo" aria-label="Redo" disabled={disabled || state.getRedoStack().isEmpty()} className={TOOL_BUTTON} onMouseDown={keepSelection} onClick={() => change(EditorState.redo(state))}>
                        <Redo2Icon className="h-4 w-4" aria-hidden />
                    </button>
                    {TOOLS.map((t, i) => (
                        <Fragment key={t.style}>
                            {/* Undo and redo | bold | the lists. */}
                            {i < 2 && <Divider />}
                            <button
                                type="button"
                                title={t.label}
                                aria-label={t.label}
                                aria-pressed={active(t)}
                                disabled={disabled}
                                className={cn(TOOL_BUTTON, active(t) && "bg-gray-300 text-gray-900")}
                                onMouseDown={keepSelection}
                                onClick={() => change(t.kind === "inline" ? RichUtils.toggleInlineStyle(state, t.style) : RichUtils.toggleBlockType(state, t.style))}
                            >
                                <t.Icon className="h-4 w-4" aria-hidden />
                            </button>
                        </Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
}
